import { AddLink, ArrowDownward, ArrowUpward, Delete, Edit, Refresh } from '@mui/icons-material'
import { Badge, Box, Button, ButtonGroup, Card, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Input, InputLabel, List, ListItem, ListItemText, ListSubheader, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectAuthUser } from '../auth/AuthSlice'
import { clearInvitedUsers, inviteUsersAsync, selectAddededUsers, selectFaultedUsers, selectInviteFault, selectInviteStatus, selectInviteUsers } from '../invite/InviteSlice'
import EmailList from '../utils/EmailList'
import { addUserToOrgAsync, autoAddUserToOrgAsync, fetchAllUsersOfOrgAsync, fetchInvitedUsersOfOrgAsync, pickAdminOrg, removeUserFromOrgAsync, selectAdminOrgStatus, selectAdminOrgUsers, selectAllAdminOrgs, selectAutoAddResult, selectInvitedUsers, updateOrgUserPaidTierLimitAsync } from './AdminOrgSlice'

const AdminOrgUserMng = () => {
  const { oid } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectAuthUser)
  const adminOrgs = useAppSelector(selectAllAdminOrgs)
  const orgUsers = useAppSelector(selectAdminOrgUsers)
  const adminOrgStatus = useAppSelector(selectAdminOrgStatus)
  const autoAddUserToOrgResult = useAppSelector(selectAutoAddResult)
  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState('userEmail,desc')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(100)
  const [invitedSort, setInvitedSort] = useState('userEmail,desc')
  const [invitedPage, setInvitedPage] = useState(0)
  const [invitedSize, setInvitedSize] = useState(100)
  const [orgName, setOrgName] = useState<string>('')
  const [addedOrDeletedUser, setAddedOrDeletedUser] = useState<string>('')
  const [addUserEmail, setAddUserEmail] = useState<string>('')
  const [addUserRole, setAddUserRole] = useState<string>('ROLE_ORG_USER')

  const [showAddUser, setShowAddUser] = useState(false)
  const [showEditOrgPaidTier, setShowEditOrgPaidTier] = useState(false);
  const [currentEditOrgPaidTierUser, setCurrentEditOrgPaidTierUser] = useState<any>(null);

  const [showInviteUser, setInviteUser] = useState(false)
  const inviteUsers = useAppSelector(selectInviteUsers)
  const invitedUsers = useAppSelector(selectInvitedUsers) //previously invited
  const addedUsers = useAppSelector(selectAddededUsers)
  const faultedUsers = useAppSelector(selectFaultedUsers)
  const inviteFault = useAppSelector(selectInviteFault)
  const inviteStatus = useAppSelector(selectInviteStatus)

  useEffect(() => {
    const selectedAdminOrg = oid && adminOrgs.content.find((u) => u.id === parseInt(oid))
    if (selectedAdminOrg) {
      setOrgName(selectedAdminOrg.name)
      dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } }))
      dispatch(fetchInvitedUsersOfOrgAsync({ orgId: oid, page: { page: invitedPage, size: invitedSize, sort: invitedSort } }))
      dispatch(pickAdminOrg(selectedAdminOrg))
    }
  }, [oid, adminOrgs])

  interface EditOrgPaidTierDialogProps {
    showEditOrgPaidTier: boolean;
    user: any;
    fullOrgPaidTierCount: number | undefined;
  }
  const EditOrgPaidTierDialog = (props: EditOrgPaidTierDialogProps) => {
    const [currentLimit, setCurrentLimit] = useState<number>(props.user?.orgPaidTierCount)
    return <Dialog
      open={props.showEditOrgPaidTier}
      onClose={() => setShowEditOrgPaidTier(false)}
    >
      <DialogTitle>Set Org level limit for <i>{props.user?.userEmail}</i></DialogTitle>
      <DialogContent>
        <DialogContentText>
          Enter a limit the number of reports this user can use up out of overall org limit. Should be less than the org limit,<strong> {props.fullOrgPaidTierCount}</strong>.
        </DialogContentText>
        <TextField
          autoFocus
          required
          margin="dense"
          id="org-paid-tier-count"
          name="orgPaidTierCount"
          label="Org Limit for this user"
          type="number"
          defaultValue={props.user?.orgPaidTierCount}
          fullWidth
          variant="standard"
          onChange={(e) => {
            setCurrentLimit(parseInt(e.target.value))
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowEditOrgPaidTier(false)}>Cancel</Button>
        <Button onClick={() => {
          if (oid) {
            dispatch(updateOrgUserPaidTierLimitAsync({ orgId: oid, userEmail: props.user?.userEmail, paidTierLimit: undefined }))
          }
          setShowEditOrgPaidTier(false)
        }}>Remove limit <Delete /></Button>
        <Button onClick={() => {
          if (oid && props.fullOrgPaidTierCount && props.fullOrgPaidTierCount > currentLimit) {
            dispatch(updateOrgUserPaidTierLimitAsync({ orgId: oid, userEmail: props.user?.userEmail, paidTierLimit: currentLimit }))
          }
          setShowEditOrgPaidTier(false)
        }}>Save</Button>
      </DialogActions>
    </Dialog>
  }

  interface UserRowProps {
    user: any;
    onDelete?: any;
    onShowEditOrgPaidTier?: any;
    fullOrgPaidTierCount?: number | undefined;
  }
  const UserRow = (props: UserRowProps) => {
    return <TableRow key={props.user.id}>
      <TableCell>
        {props.user.userEmail}
      </TableCell>
      <TableCell sx={{ whiteSpace: "normal", wordWrap: "break-word" }}>
        {props.user.role?.split(',').filter((e: any) => e.indexOf('_ORG_') > -1).map((e: any) => e.trim()).join(' | \n')}
      </TableCell>
      {props.fullOrgPaidTierCount ? <TableCell>
        <Button disabled={props.user.role?.indexOf('ROLE_ORG_OWNER') >= 0} onClick={props.onShowEditOrgPaidTier}>
          <Badge badgeContent={props.user.orgPaidTierCount} color="secondary">
            <Edit />
          </Badge>
        </Button>
      </TableCell> : <TableCell>&nbsp;</TableCell>}
      {props.onDelete ? <TableCell>
        <Button disabled={props.user.role?.indexOf('ROLE_ORG_OWNER') >= 0} onClick={() => props.onDelete(props.user.userEmail)}><Delete /></Button>
      </TableCell> : <TableCell>&nbsp;</TableCell>}
    </TableRow>
  }

  return (<div>
    <EditOrgPaidTierDialog showEditOrgPaidTier={showEditOrgPaidTier} user={currentEditOrgPaidTierUser} fullOrgPaidTierCount={user?.org?.paidTierCount} />
    <h1>Manage Org Users of <i>{orgName}</i></h1>
    {oid && <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mb: 1 }}>
      <Button onClick={() => {
        dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } }))
        dispatch(fetchInvitedUsersOfOrgAsync({ orgId: oid, page: { page: invitedPage, size: invitedSize, sort: invitedSort } }))
      }}><Refresh /> Reload</Button>
      {/* <Button sx={{ boxShadow: showAddUser ? 1 : 0, borderColor: grey[500], borderStyle: 'solid' }} onClick={() => {
        setShowAddUser(true)
        setInviteUser(false)
      }}><Add /> Add User</Button> */}
      <Button sx={{ boxShadow: showInviteUser ? 1 : 0, borderColor: grey[500], borderStyle: 'solid' }} onClick={() => {
        setShowAddUser(false)
        setInviteUser(true)
      }}><AddLink /> Invite User</Button>
    </Box>}
    {oid && showAddUser && <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mb: 4, boxShadow: 2, p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
        <FormControl>
          <Input sx={{ minWidth: 300, marginRight: 4 }} placeholder="User Email" onChange={(e) => setAddUserEmail(e.target.value)} />
        </FormControl>
        <FormControl>
          <InputLabel id="org-user-add-role-label">Role</InputLabel>
          <Select labelId="org-user-add-role-label" id="org-user-add-role-select"
            value={addUserRole} label="Role" onChange={(e) => setAddUserRole(e.target.value)}>
            <MenuItem value={'ROLE_ORG_USER'}>Org User</MenuItem>
            <MenuItem value={'ROLE_ORG_ADMIN'}>Org Admin</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <ButtonGroup variant="text" aria-label="text button group" sx={{ marginBottom: 2 }}>
        <Button onClick={() => {
          setShowAddUser(false)
          setAddUserEmail('')
          setAddUserRole('ROLE_ORG_USER')
        }}>Cancel</Button>
        <Button onClick={() => {
          dispatch(addUserToOrgAsync({ orgId: oid, email: addUserEmail, role: addUserRole ? addUserRole : 'ROLE_ORG_USER' }))
          setTimeout(() => dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } })), 1000)
          setShowAddUser(false)
          setAddUserEmail('')
          setAddUserRole('ROLE_ORG_USER')
        }
        }>Add</Button>
        {<Button onClick={() => {
          dispatch(autoAddUserToOrgAsync({ orgId: oid, role: addUserRole ? addUserRole : 'ROLE_ORG_USER', isDryRun: true, isAutoAddFutureUsers: true }))
          setTimeout(() => dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } })), 1000)
          setAddUserEmail('')
          setAddUserRole('ROLE_ORG_USER')
        }
        }>Auto Add</Button>}
      </ButtonGroup>
      {autoAddUserToOrgResult && autoAddUserToOrgResult.success && autoAddUserToOrgResult.success.length > 0 && <Box sx={{
        display: 'flex', flexDirection: 'column', borderColor: 'grey.300',
        maxHeight: '600px', p: 3, borderRadius: 1, borderWidth: 1, borderStyle: 'solid', boxShadow: 1, overflowY: 'auto'
      }}>
        <Typography variant="h6">Following users will be affected:</Typography>
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'auto',
            maxHeight: 300,
            '& ul': { padding: 0 },
          }}
          subheader={<li />}
        >
          {['success', 'failure'].map((title, sectionId) => (
            <li key={`section-${sectionId}`}>
              <ul>
                <ListSubheader>{`${title}`}</ListSubheader>
                {(autoAddUserToOrgResult[title as keyof typeof autoAddUserToOrgResult]).map((item) => (
                  <ListItem key={`item-${sectionId}-${item}`}>
                    <ListItemText primary={`${item.userEmail}`} secondary={item.org.name} />
                  </ListItem>
                ))}
              </ul>
            </li>
          ))}
        </List>
        <Button variant='contained' onClick={() => {
          dispatch(autoAddUserToOrgAsync({ orgId: oid, role: addUserRole ? addUserRole : 'ROLE_ORG_USER', isDryRun: false, isAutoAddFutureUsers: true }))
          setTimeout(() => dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } })), 1000)
          setShowAddUser(false)
          setAddUserEmail('')
          setAddUserRole('ROLE_ORG_USER')
        }}>Confirm</Button>
      </Box>}
    </Box>}
    {showInviteUser && <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mb: 4, boxShadow: 2, p: 4 }}>
      <EmailList onInvite={(emails: string[]) => {
        dispatch(inviteUsersAsync({ emails, oid: oid ?? '' }))
      }} onClearInvited={() => {
        dispatch(clearInvitedUsers())
      }} />
      {inviteStatus === 'loading' && <Typography variant="h6">Inviting users...</Typography>}
      <Card sx={{ display: inviteUsers && inviteUsers.length > 0 ? 'flex' : 'none', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ mt: 2 }}>Invited Users</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>These users have been invited, once they accept, they will be automatically added to the Org</Typography>
        {inviteUsers.map((u, index) => <Button key={index} sx={{ px: 2, textTransform: 'none' }} variant='text' size="small">
          {u.userEmail}
        </Button>)}
      </Card>
      <Card sx={{ display: addedUsers && addedUsers.length > 0 ? 'flex' : 'none', flexDirection: 'column', mt: 4, px: 4 }}>
        <Typography variant="h6">Added Users</Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom>These users already exist in the system, they have been added to the Org</Typography>
        {addedUsers.map((u, index) => <Button key={index} sx={{ px: 2, textTransform: 'none' }} variant='text' size="small">
          {u.userEmail}
        </Button>)}
      </Card>
      <Card sx={{ display: faultedUsers && faultedUsers.length > 0 ? 'flex' : 'none', flexDirection: 'column', mt: 4, px: 4 }}>
        <Typography variant="h6" sx={{ mt: 2 }}>Fault Users</Typography>
        <Typography variant="caption" color="text.secondary">These users already exist in the system and part of another Org, they will be ignored</Typography>
        {faultedUsers.map((u, index) => <Button key={index} sx={{ px: 2, textTransform: 'none' }} variant='text' size="small">
          {u.userEmail} ({u.org.name})
        </Button>)}
      </Card>
      {(inviteUsers?.length || addedUsers?.length || faultedUsers?.length) ? <Button variant='contained' onClick={() => {
        dispatch(clearInvitedUsers())
        setInviteUser(false)
      }}>Done</Button> : <></>}
      <Typography variant="caption" color="#C55">
        {inviteFault}
      </Typography>
    </Box>}
    <Input placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} />
    {oid && orgUsers.content.length > 0 && <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell><Button disabled={adminOrgStatus === 'loading'} onClick={() => {
              var tmp = ''
              if (sort.startsWith('userEmail') && sort.endsWith('desc')) {
                tmp = 'userEmail,asc'
              } else {
                tmp = 'userEmail,desc'
              }
              setSort(tmp)
              dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort: tmp } }))
            }}>Email
              {sort.startsWith('userEmail') ? sort === 'userEmail,desc' ? <ArrowDownward onClick={() => setSort('userEmail,asc')} /> : <ArrowUpward onClick={() => setSort('userEmail,desc')} /> : <></>}
            </Button></TableCell>
            <TableCell>Roles</TableCell>
            {user?.org?.paidTierCount && <TableCell>
              <Badge badgeContent={user?.org?.paidTierCount} color="secondary">
                Org limit
              </Badge>
            </TableCell>}
            <TableCell>{orgUsers.content.length}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orgUsers.content.filter((userElem) => filter.length == 0
            || (userElem.userEmail && userElem.userEmail?.toLowerCase().indexOf(filter.toLowerCase()) > -1))
            .map((userElem: any, index: number) => <UserRow fullOrgPaidTierCount={user?.org?.paidTierCount} key={index} user={userElem} onDelete={(userEmail: string) => {
              dispatch(removeUserFromOrgAsync({ orgId: oid, userEmail }))
              setTimeout(() => dispatch(fetchAllUsersOfOrgAsync({ orgId: oid, page: { page, size, sort } })), 1000)
            }} onShowEditOrgPaidTier={() => {
              setCurrentEditOrgPaidTierUser(userElem)
              setShowEditOrgPaidTier(userElem)
            }} />)}
          {invitedUsers && invitedUsers?.size ? <TableRow>
            <TableCell colSpan={4}>
              <Typography variant="h6">
                Pending acceptance
              </Typography>
            </TableCell>
          </TableRow> : <></>}

          {invitedUsers?.content.filter((userElem) => filter.length == 0
            || (userElem.userEmail && userElem.userEmail?.toLowerCase().indexOf(filter.toLowerCase()) > -1))
            .map((u, index) => <UserRow fullOrgPaidTierCount={0} key={index} user={{ ...u, role: u.orgRole }}></UserRow>)}

          {/* <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mb: 4, boxShadow: 2, p: 4 }}>
            <Typography variant="h6">Invited users</Typography>
            <Card sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mt: 2 }}>Invited Users</Typography>
              <Typography variant="caption" sx={{ px: 2 }} color="text.secondary">These users have been invited, once they accept, they will be automatically added to the Org</Typography>
              {invitedUsers?.content.map((u, index) => <Button key={index} sx={{ px: 2, textTransform: 'none' }} variant='text' size="small">
                {u.userEmail} | {u.createdTime}
              </Button>)}
            </Card>
          </Box> */}
        </TableBody>
      </Table>
    </TableContainer>}
  </div>)
}
export default AdminOrgUserMng
